import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLocale } from '@parentpath/i18n';
import type { BlogPost, BlogCategory, BlogPostWithCategory } from '../types';

interface UseBlogPostsOptions {
  ssw?: number;
  limit?: number;
}

interface UseBlogPostsReturn {
  posts: BlogPostWithCategory[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch blog posts relevant to the current SSW and pregnancy type
 */
export function useBlogPosts(options: UseBlogPostsOptions = {}): UseBlogPostsReturn {
  const { ssw, limit = 5 } = options;
  const { currentPregnancy } = usePregnancyStore();
  const { locale } = useLocale();
  const [posts, setPosts] = useState<BlogPostWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    if (!ssw) {
      setPosts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use the RPC function to get SSW-relevant posts
      const { data: postsData, error: postsError } = await supabase.rpc('get_blog_posts_for_ssw', {
        p_ssw: ssw,
        p_is_risk_pregnancy: currentPregnancy?.is_high_risk || false,
        p_child_count: currentPregnancy?.child_count || 1,
        p_limit: limit,
        p_locale: locale,
      });

      if (postsError) {
        throw postsError;
      }

      if (!postsData || postsData.length === 0) {
        setPosts([]);
        return;
      }

      // Fetch categories for the posts
      const categoryIds = [...new Set(postsData.map((p: BlogPost) => p.category_id).filter(Boolean))];

      let categoriesMap: Record<string, BlogCategory> = {};
      if (categoryIds.length > 0) {
        const { data: categoriesData } = await supabase
          .from('blog_categories')
          .select('*')
          .eq('locale', locale)
          .in('id', categoryIds);

        if (categoriesData) {
          categoriesMap = categoriesData.reduce((acc, cat) => {
            acc[cat.id] = cat;
            return acc;
          }, {} as Record<string, BlogCategory>);
        }
      }

      // Combine posts with their categories
      const postsWithCategories: BlogPostWithCategory[] = postsData.map((post: BlogPost) => ({
        ...post,
        category: post.category_id ? categoriesMap[post.category_id] || null : null,
      }));

      setPosts(postsWithCategories);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Blogbeiträge');
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  }, [ssw, currentPregnancy?.is_high_risk, currentPregnancy?.child_count, limit, locale]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return {
    posts,
    isLoading,
    error,
    refetch: fetchPosts,
  };
}

/**
 * Hook to fetch blog posts for baby mode, filtered by baby age in days
 */
export function useBabyBlogPosts(options: { babyAgeDays?: number; limit?: number } = {}): UseBlogPostsReturn {
  const { babyAgeDays, limit = 5 } = options;
  const { locale } = useLocale();
  const [posts, setPosts] = useState<BlogPostWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    if (babyAgeDays == null) {
      setPosts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: postsData, error: postsError } = await supabase.rpc('get_blog_posts_for_baby', {
        p_baby_age_days: babyAgeDays,
        p_limit: limit,
        p_locale: locale,
      });

      if (postsError) throw postsError;

      if (!postsData || postsData.length === 0) {
        setPosts([]);
        return;
      }

      // Fetch categories for the posts
      const categoryIds = [...new Set(postsData.map((p: BlogPost) => p.category_id).filter(Boolean))];

      let categoriesMap: Record<string, BlogCategory> = {};
      if (categoryIds.length > 0) {
        const { data: categoriesData } = await supabase
          .from('blog_categories')
          .select('*')
          .eq('locale', locale)
          .in('id', categoryIds);

        if (categoriesData) {
          categoriesMap = categoriesData.reduce((acc, cat) => {
            acc[cat.id] = cat;
            return acc;
          }, {} as Record<string, BlogCategory>);
        }
      }

      const postsWithCategories: BlogPostWithCategory[] = postsData.map((post: BlogPost) => ({
        ...post,
        category: post.category_id ? categoriesMap[post.category_id] || null : null,
      }));

      setPosts(postsWithCategories);
    } catch (err) {
      console.error('Error fetching baby blog posts:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Blogbeiträge');
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  }, [babyAgeDays, limit, locale]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return {
    posts,
    isLoading,
    error,
    refetch: fetchPosts,
  };
}

/**
 * Hook to fetch a single blog post by slug
 */
export function useBlogPost(slug: string | undefined) {
  const [post, setPost] = useState<BlogPostWithCategory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPost() {
      if (!slug) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Fetch post with category
        const { data, error: fetchError } = await supabase
          .from('blog_posts')
          .select(`
            *,
            category:blog_categories(*)
          `)
          .eq('slug', slug)
          .eq('is_published', true)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Beitrag nicht gefunden');
          } else {
            throw fetchError;
          }
          return;
        }

        setPost(data as BlogPostWithCategory);

        // Increment view count (fire and forget)
        supabase.rpc('increment_blog_view_count', { post_id: data.id }).then(() => {
          // View count updated
        });
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError(err instanceof Error ? err.message : 'Fehler beim Laden des Beitrags');
      } finally {
        setIsLoading(false);
      }
    }

    fetchPost();
  }, [slug]);

  return { post, isLoading, error };
}

/**
 * Hook to fetch all categories
 */
export function useBlogCategories() {
  const { locale } = useLocale();
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const { data, error } = await supabase
          .from('blog_categories')
          .select('*')
          .eq('is_active', true)
          .eq('locale', locale)
          .order('sort_order');

        if (error) throw error;
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchCategories();
  }, [locale]);

  return { categories, isLoading };
}
